/**
 * AuthSheet — Strategy A PR-A8 (real-audit follow-up).
 *
 * Mirrors H5 `#authWindow` (`index.html:155-245`) inner panel content:
 *
 *   <header class="compose-head">
 *     <div class="head-meta"><p class="eyebrow">CCNU Gate</p><h3>…</h3></div>
 *     <button class="icon-button" data-close><span/><span/></button>
 *   </header>
 *   <div class="auth-body">
 *     <div class="auth-campus-note">…</div>
 *     <div class="auth-tabs">注册 / 登录</div>
 *     <form class="auth-form">register fields</form>
 *     <form class="auth-form" hidden>login fields</form>
 *   </div>
 *
 * The outer `<section class="auth-panel">` is provided by the shared `<Sheet
 * overlayKind="auth" panelClass="auth-panel">` wrapper.
 *
 * Field structure uses H5 classes verbatim:
 *   .field.auth-field / .auth-email-box / .auth-at / .auth-domain-switch /
 *   .auth-username-box / .auth-username-prefix / .auth-account-switch /
 *   .auth-account-switch-divider / .auth-auto-login(-mark) / .auth-code-row /
 *   .form-message / .submit-button / .auth-secondary-button
 */

export type AuthSheetMode = "register" | "login";
export type LoginAccountMode = "email" | "username";

export type DomainOption = {
  id: string;
  label: string;
};

export type RegisterValues = {
  emailLocal: string;
  code: string;
  username: string;
  displayName: string;
  password: string;
};

export type LoginValues = {
  emailLocal: string;
  username: string;
  password: string;
};

export type AuthSheetProps = {
  mode: AuthSheetMode;
  loginAccountMode?: LoginAccountMode;
  domains: DomainOption[];
  registerDomain: string;
  loginDomain: string;
  registerValues: RegisterValues;
  loginValues: LoginValues;
  message?: string;
  messageTone?: "neutral" | "error" | "success";
  codeCooldownSeconds?: number;
  submitting?: boolean;
  onClose?: () => void;
  onModeChange?: (mode: AuthSheetMode) => void;
  onLoginAccountModeChange?: (mode: LoginAccountMode) => void;
  onRegisterDomainChange?: (domain: string) => void;
  onLoginDomainChange?: (domain: string) => void;
  onRegisterChange?: (field: keyof RegisterValues, value: string) => void;
  onLoginChange?: (field: keyof LoginValues, value: string) => void;
  onRequestCode?: () => void;
  onSubmitRegister?: () => void;
  onSubmitLogin?: () => void;
};

export function AuthSheet({
  mode,
  loginAccountMode = "email",
  domains,
  registerDomain,
  loginDomain,
  registerValues,
  loginValues,
  message,
  messageTone = "neutral",
  codeCooldownSeconds = 0,
  submitting = false,
  onClose,
  onModeChange,
  onLoginAccountModeChange,
  onRegisterDomainChange,
  onLoginDomainChange,
  onRegisterChange,
  onLoginChange,
  onRequestCode,
  onSubmitRegister,
  onSubmitLogin,
}: AuthSheetProps) {
  const isRegister = mode === "register";
  const codeButtonText = codeCooldownSeconds > 0 ? `${codeCooldownSeconds}s` : "发送验证码";

  return (
    <>
      <header className="compose-head">
        <div className="head-meta">
          <p className="eyebrow">CCNU Gate</p>
          <h3 id="auth-sheet-title">{isRegister ? "华师邮箱注册" : "华师邮箱登录"}</h3>
        </div>
        <button
          type="button"
          className="icon-button"
          data-close
          aria-label="关闭登录注册"
          onClick={onClose}
        >
          <span />
          <span />
        </button>
      </header>

      <div className="auth-body">
        <div className="auth-campus-note">
          <span>仅开放华中师范大学邮箱</span>
          <strong>@mails.ccnu.edu.cn / @ccnu.edu.cn</strong>
        </div>

        <div className="auth-tabs" role="tablist" aria-label="登录注册切换">
          <button
            type="button"
            role="tab"
            className={isRegister ? "is-active" : ""}
            aria-selected={isRegister}
            onClick={() => onModeChange?.("register")}
          >
            注册
          </button>
          <button
            type="button"
            role="tab"
            className={!isRegister ? "is-active" : ""}
            aria-selected={!isRegister}
            onClick={() => onModeChange?.("login")}
          >
            登录
          </button>
        </div>

        {isRegister ? (
        <form
          className="auth-form"
          aria-label="注册"
          role="tabpanel"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmitRegister?.();
          }}
        >
          <EmailField
            label="华师邮箱"
            extraClass="auth-email-field"
            value={registerValues.emailLocal}
            domains={domains}
            selectedDomain={registerDomain}
            onValueChange={(value) => onRegisterChange?.("emailLocal", value)}
            onDomainChange={onRegisterDomainChange}
          />

          <div className="auth-code-row">
            <label className="field auth-field">
              <span>验证码</span>
              <input
                aria-label="验证码"
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                placeholder="6 位数字"
                value={registerValues.code}
                onChange={(event) => onRegisterChange?.("code", event.target.value)}
              />
            </label>
            <button
              type="button"
              className="auth-secondary-button"
              disabled={submitting || codeCooldownSeconds > 0}
              onClick={onRequestCode}
            >
              {codeButtonText}
            </button>
          </div>

          <UsernameField
            label="用户名"
            placeholder="2-24 位字母数字"
            value={registerValues.username}
            onChange={(value) => onRegisterChange?.("username", value)}
          />

          <label className="field auth-field">
            <span>昵称</span>
            <input
              aria-label="昵称"
              type="text"
              autoComplete="nickname"
              placeholder="你想被怎么称呼"
              value={registerValues.displayName}
              onChange={(event) => onRegisterChange?.("displayName", event.target.value)}
            />
          </label>

          <label className="field auth-field">
            <span>密码</span>
            <input
              aria-label="密码"
              type="password"
              autoComplete="new-password"
              placeholder="至少 6 位"
              value={registerValues.password}
              onChange={(event) => onRegisterChange?.("password", event.target.value)}
            />
          </label>

          <div className="auth-auto-login" aria-live="polite">
            <span className="auth-auto-login-mark" aria-hidden="true" />
            <span>注册完成后自动进入，不需要再登录一次。</span>
          </div>

          <p className="form-message" role="status" data-tone={messageTone}>
            {message || ""}
          </p>

          <button className="submit-button" type="submit" disabled={submitting}>
            注册并自动登录
          </button>
        </form>
        ) : (
        <form
          className="auth-form"
          aria-label="登录"
          role="tabpanel"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmitLogin?.();
          }}
        >
          <div className="field auth-field auth-login-account-field">
            <span className="auth-account-switch" role="radiogroup" aria-label="登录账号类型">
              <button
                type="button"
                role="radio"
                className={loginAccountMode === "email" ? "is-active" : ""}
                aria-checked={loginAccountMode === "email"}
                onClick={() => onLoginAccountModeChange?.("email")}
              >
                华师邮箱
              </button>
              <span className="auth-account-switch-divider" aria-hidden="true">
                /
              </span>
              <button
                type="button"
                role="radio"
                className={loginAccountMode === "username" ? "is-active" : ""}
                aria-checked={loginAccountMode === "username"}
                onClick={() => onLoginAccountModeChange?.("username")}
              >
                用户名
              </button>
            </span>

            {loginAccountMode === "email" ? (
              <label className="auth-email-box">
                <input
                  aria-label="华师邮箱前缀"
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="邮箱名前缀"
                  value={loginValues.emailLocal}
                  onChange={(event) => onLoginChange?.("emailLocal", event.target.value)}
                />
                <span className="auth-at" aria-hidden="true">
                  @
                </span>
                <div
                  className="auth-domain-switch"
                  role="radiogroup"
                  aria-label="登录邮箱域名"
                >
                  {domains.map((domain) => (
                    <button
                      key={domain.id}
                      type="button"
                      role="radio"
                      className={domain.id === loginDomain ? "is-active" : ""}
                      aria-checked={domain.id === loginDomain}
                      onClick={() => onLoginDomainChange?.(domain.id)}
                    >
                      {domain.label}
                    </button>
                  ))}
                </div>
              </label>
            ) : (
              <label className="auth-username-box">
                <span className="auth-username-prefix" aria-hidden="true">
                  @
                </span>
                <input
                  aria-label="用户名"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="你的用户名"
                  value={loginValues.username}
                  onChange={(event) => onLoginChange?.("username", event.target.value)}
                />
              </label>
            )}
          </div>

          <label className="field auth-field">
            <span>密码</span>
            <input
              aria-label="密码"
              type="password"
              autoComplete="current-password"
              placeholder="你的密码"
              value={loginValues.password}
              onChange={(event) => onLoginChange?.("password", event.target.value)}
            />
          </label>

          <p className="form-message" role="status" data-tone={messageTone}>
            {message || ""}
          </p>

          <button className="submit-button" type="submit" disabled={submitting}>
            登录
          </button>
        </form>
        )}
      </div>
    </>
  );
}

function UsernameField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange?: (value: string) => void;
}) {
  return (
    <label className="field auth-field">
      <span>{label}</span>
      <span className="auth-username-box">
        <span className="auth-username-prefix" aria-hidden="true">
          @
        </span>
        <input
          aria-label={label}
          type="text"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
        />
      </span>
    </label>
  );
}

function EmailField({
  label,
  extraClass = "",
  value,
  domains,
  selectedDomain,
  onValueChange,
  onDomainChange,
}: {
  label: string;
  extraClass?: string;
  value: string;
  domains: DomainOption[];
  selectedDomain: string;
  onValueChange?: (value: string) => void;
  onDomainChange?: (domain: string) => void;
}) {
  const classes = ["field", "auth-field", extraClass].filter(Boolean).join(" ");
  return (
    <label className={classes}>
      <span>{label}</span>
      <div className="auth-email-box">
        <input
          aria-label={`${label}前缀`}
          type="text"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="邮箱名前缀"
          value={value}
          onChange={(event) => onValueChange?.(event.target.value)}
        />
        <span className="auth-at" aria-hidden="true">
          @
        </span>
        <div
          className="auth-domain-switch"
          role="radiogroup"
          aria-label={`${label}域名`}
        >
          {domains.map((domain) => (
            <button
              key={domain.id}
              type="button"
              role="radio"
              className={domain.id === selectedDomain ? "is-active" : ""}
              aria-checked={domain.id === selectedDomain}
              onClick={() => onDomainChange?.(domain.id)}
            >
              {domain.label}
            </button>
          ))}
        </div>
      </div>
    </label>
  );
}
